/**
 * MSAL-Konfiguration fuer die Anmeldung an Microsoft Entra ID.
 *
 * Alle Werte stammen aus Umgebungsvariablen (Vite `VITE_*`). Sie sind
 * nicht geheim: Tenant-Id, Client-Id und Bereich sind oeffentliche Bezeichner
 * einer oeffentlichen Clientanwendung (SPA). Ein Client Secret existiert fuer
 * SPAs nicht und darf hier unter keinen Umstaenden hinterlegt werden – die
 * Anmeldung nutzt den Authorization Code Flow mit PKCE.
 *
 * WICHTIG – warum die Konfiguration verzoegert aufgebaut wird:
 * Frueher wurden die Umgebungsvariablen im Modulrumpf geprueft. Nach
 * ES-Modul-Semantik laeuft der Rumpf eines statisch importierten Moduls jedoch,
 * bevor der importierende Code ausgefuehrt wird. Ein fehlender Wert warf damit,
 * bevor `bootstrap()` ueberhaupt startete – die dortige Fehlerbehandlung konnte
 * nicht greifen, und der Benutzer sah eine weisse Seite statt einer Meldung.
 * Deshalb wird die Konfiguration erst beim ersten Aufruf von `getAuthConfig()`
 * erzeugt (und danach zwischengespeichert), sodass der Fehler an einer Stelle
 * entsteht, an der er abgefangen und angezeigt werden kann.
 */
import { LogLevel, type Configuration, type PopupRequest, type SilentRequest } from '@azure/msal-browser';

export class AuthConfigError extends Error {
  readonly variableName: string;
  constructor(variableName: string) {
    super(
      `Die Umgebungsvariable ${variableName} ist nicht gesetzt. Bitte konfigurieren Sie die Anwendung gemäß web/.env.example.`,
    );
    this.name = 'AuthConfigError';
    this.variableName = variableName;
  }
}

function requireEnv(name: string): string {
  const value = import.meta.env[name as keyof ImportMetaEnv] as string | undefined;
  if (!value || value.trim() === '') throw new AuthConfigError(name);
  return value.trim();
}

export interface AuthConfig {
  msalConfig: Configuration;
  /** Leere Seite fuer die stille Erneuerung; muss in Entra registriert sein. */
  silentRedirectUri: string;
  /** Anmeldung: nur der eigene API-Bereich, keine Graph-Berechtigungen im Browser. */
  loginRequest: PopupRequest;
  silentRequest: SilentRequest;
  /** Bereich, den die SPA fuer den Zugriff auf die eigene API anfordert. */
  apiScope: string;
  tenantId: string;
}

let cached: AuthConfig | undefined;

/**
 * Baut die Konfiguration beim ersten Aufruf und liefert sie danach aus dem
 * Zwischenspeicher. Wirft `AuthConfigError`, wenn ein Wert fehlt.
 */
export function getAuthConfig(): AuthConfig {
  if (cached) return cached;

  const authorityHost = (import.meta.env.VITE_ENTRA_AUTHORITY_HOST ?? 'https://login.microsoftonline.com').replace(
    /\/$/,
    '',
  );
  const tenantId = requireEnv('VITE_ENTRA_TENANT_ID');
  const clientId = requireEnv('VITE_ENTRA_CLIENT_ID');
  const apiScope = requireEnv('VITE_API_SCOPE');

  // Ableitung aus dem Ursprung, damit dieselbe Datei in jeder Umgebung passt
  // (lokal, Vorschau, Produktion) und kein weiterer Konfigurationswert noetig ist.
  const silentRedirectUri = `${window.location.origin}/blank.html`;

  const msalConfig: Configuration = {
    auth: {
      clientId,
      // Einzelmandanten-Autoritaet: Konten anderer Tenants und private
      // Microsoft-Konten werden dadurch bereits beim Anmeldevorgang abgewiesen.
      authority: `${authorityHost}/${tenantId}`,
      knownAuthorities: [new URL(authorityHost).host],
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      // sessionStorage statt localStorage: Die Sitzung endet mit dem Schliessen
      // des Browser-Tabs, was auf gemeinsam genutzten Geraeten sicherer ist.
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: false,
    },
    system: {
      allowNativeBroker: false,
      loggerOptions: {
        logLevel: import.meta.env.DEV ? LogLevel.Warning : LogLevel.Error,
        piiLoggingEnabled: false,
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) return;
          if (level === LogLevel.Error) console.error('[MSAL]', message);
          else if (level === LogLevel.Warning) console.warn('[MSAL]', message);
        },
      },
    },
  };

  cached = {
    msalConfig,
    silentRedirectUri,
    apiScope,
    tenantId,
    loginRequest: {
      scopes: [apiScope],
      // Kein domain_hint: Der Parameter erwartet einen Domaennamen (z. B.
      // "firma.de"). Der frueher gesetzte Wert "organizations" ist kein
      // Domaenenname, sondern ein Tenant-Segment einer Autoritaet, und wurde
      // vom Anmeldeendpunkt mit AADSTS90013 ("Invalid input received from the
      // user") abgewiesen. Die Beschraenkung auf den eigenen Tenant leistet
      // bereits die Einzelmandanten-Autoritaet oben; ein Hinweis ist dafuer
      // nicht erforderlich.
    },
    silentRequest: {
      scopes: [apiScope],
      // Die stille Erneuerung laeuft in einem versteckten iframe. Zeigte die
      // Umleitung auf die Anwendung, laedt das iframe die gesamte SPA und der
      // Vorgang laeuft in das Zeitfenster von sechs Sekunden – Fehlerbild
      // "monitor_window_timeout" bei voellig gueltiger Sitzung. Eine leere
      // Seite ist sofort da. Sie muss in der App-Registrierung als
      // SPA-Umleitungs-URI eingetragen sein.
      redirectUri: silentRedirectUri,
    },
  };
  return cached;
}

/** Nur fuer Tests. */
export function resetAuthConfigCache(): void {
  cached = undefined;
}
